import React, { useEffect, useState, useContext } from 'react';
import { Snackbar, Alert } from '@mui/material';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import BrutalistInput from '../shared/BrutalistInput';
import BrutalistButton2 from '../shared/BrutalistButton2';
import './CriarCampanha.css';
import { listarSegmentacoes, criarCampanha, atualizarCampanha } from '../../../../services/api';
import { TabContext } from '../SmartCampaignsDashboard';
import { formatMarkdownToSafeHtml } from '../../../../utils/html';

const CriarCampanha = ({ campanhaEdit, onClose }) => {
  const { setTab } = useContext(TabContext);
  const [campanhaNome, setCampanhaNome] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [imagem, setImagem] = useState(null);
  const [preview, setPreview] = useState(null);
  const [ruido, setRuido] = useState('');
  const [tentativas, setTentativas] = useState('');
  const [segmentoSelecionado, setSegmentoSelecionado] = useState('');
  const [segmentacoes, setSegmentacoes] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [fieldErrors, setFieldErrors] = useState({});
  const diasDaSemana = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

  const [openHorarioModal, setOpenHorarioModal] = useState(false);

  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('19:00');
  const [selectedDays, setSelectedDays] = useState(new Set()); 

  useEffect(() => {
    (async () => {
      try {
        const segs = await listarSegmentacoes();
        setSegmentacoes(segs || []);
      } catch (e) {
        setSegmentacoes([]);
      }
    })();
  }, []);

  useEffect(() => {
    if (!campanhaEdit) {
      setSelectedDays(new Set());
      return;
    }
    setCampanhaNome(campanhaEdit.nome || '');
    setMensagem(campanhaEdit.mensagem || '');
    setRuido(String(campanhaEdit.ruido ?? ''));
    setTentativas(String(campanhaEdit.tentativas ?? ''));
    setSegmentoSelecionado(campanhaEdit.segmentacao_id || '');
    setPreview(campanhaEdit.imagem_url || null);
    setImagem(null);

    if (Array.isArray(campanhaEdit.horarios) && campanhaEdit.horarios.length > 0) {
      const idxToName = { 0: 'Domingo', 1: 'Segunda', 2: 'Terça', 3: 'Quarta', 4: 'Quinta', 5: 'Sexta', 6: 'Sábado' };
      const ativos = campanhaEdit.horarios.filter(h => h.ativo !== false);
      if (ativos.length > 0) {
        setStartTime((ativos[0].hora_inicio || '').slice(0, 5) || '09:00');
        setEndTime((ativos[0].hora_fim || '').slice(0, 5) || '19:00');
        const days = new Set(ativos.map(h => idxToName[h.dia_semana]).filter(Boolean));
        setSelectedDays(days);
      } else {
        setSelectedDays(new Set());
      }
    } else {
      setSelectedDays(new Set());
    }
  }, [campanhaEdit]);

  const handleImagemChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setImagem(null);
      setPreview(campanhaEdit?.imagem_url || null);
      return;
    }
    if (!file.type.startsWith('image/')) {
      setSnackbar({ open: true, message: 'Selecione apenas arquivos de imagem.', severity: 'warning' });
      setImagem(null);
      setPreview(campanhaEdit?.imagem_url || null);
      return;
    }
    setImagem(file);
    setPreview(URL.createObjectURL(file));
  };

  const clearFieldError = (key) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const toggleDay = (dia) => {
    setSelectedDays(prev => {
      const next = new Set(prev);
      if (next.has(dia)) next.delete(dia);
      else next.add(dia);
      return next;
    });
    clearFieldError('dias');
  };

  const salvarCampanha = async () => {
    const nameToIdx = { Domingo: 0, Segunda: 1, Terça: 2, Quarta: 3, Quinta: 4, Sexta: 5, Sábado: 6 };
    const diasSelecionados = Array.from(selectedDays);
    const errors = {};

    if (!campanhaNome) errors.nome = 'Informe o nome da campanha.';
    if (!segmentoSelecionado) errors.segmento = 'Selecione uma segmentação.';
    if (!mensagem || !mensagem.includes('$cliente')) {
      errors.mensagem = 'A mensagem deve conter o placeholder $cliente. Ex: Olá, $cliente!';
    }
    if (!diasSelecionados.length) errors.dias = 'Selecione ao menos um dia da semana.';
    if (!startTime || !endTime) errors.horario = 'Defina o horário de início e fim.';

    setFieldErrors(errors);
    if (Object.keys(errors).length) {
      setSnackbar({
        open: true,
        message: Object.values(errors)[0],
        severity: 'warning',
      });
      return;
    }

    const horariosPayload = diasSelecionados.map(dia => ({
      dia_semana: nameToIdx[dia],
      ativo: true,
      hora_inicio: startTime,
      hora_fim: endTime,
    }));

    const formData = new FormData();
    formData.append('nome', campanhaNome);
    formData.append('segmentacao_id', segmentoSelecionado);
    formData.append('mensagem', mensagem);
    formData.append('ruido', parseInt(ruido || '0', 10));
    formData.append('tentativas', parseInt(tentativas || '1', 10));
    formData.append('horarios', JSON.stringify(horariosPayload));
    if (imagem) formData.append('imagem', imagem);

    try {
      if (campanhaEdit?.id) {
        await atualizarCampanha(campanhaEdit.id, formData, true);
        setSnackbar({ open: true, message: 'Campanha atualizada com sucesso!', severity: 'success' });
      } else {
        await criarCampanha(formData, true);
        setSnackbar({ open: true, message: 'Campanha criada com sucesso!', severity: 'success' });
      }
      if (onClose) onClose(); else setTab('campanhas');
    } catch (e) {
      console.error('Erro ao salvar campanha:', e);
      setSnackbar({ open: true, message: 'Erro ao salvar campanha.', severity: 'error' });
    }
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
      <div className="card" style={{ width: '100%', maxWidth: '1300px', display: 'flex', gap: '2rem' }}>
        <div style={{ flex: 1 }}>
          <span className="card__title">{campanhaEdit?.id ? 'Editar Campanha' : 'Nova Campanha'}</span>

          <div className="card__form">
            <BrutalistInput
              id="campanha-nome"
              placeholder="Ex: Promoção Fim de Semana"
              label="NOME DA CAMPANHA"
              value={campanhaNome}
              error={fieldErrors.nome}
              onChange={(e) => {
                setCampanhaNome(e.target.value);
                clearFieldError('nome');
              }}
            />

            {/* Segmentação */}
            <div>
              <label className="brutalist-label" htmlFor="campanha-segmento">SEGMENTAÇÃO</label>
              <select
                id="campanha-segmento"
                className="brutalist-select"
                value={segmentoSelecionado}
                aria-invalid={Boolean(fieldErrors.segmento) || undefined}
                onChange={(e) => {
                  setSegmentoSelecionado(e.target.value);
                  clearFieldError('segmento');
                }}
              >
                <option value="">Selecione uma segmentação</option>
                {segmentacoes.map((seg) => (
                  <option key={seg.id} value={seg.id}>{seg.nome}</option>
                ))}
              </select>
              {fieldErrors.segmento ? (
                <p role="alert" style={{ color: '#b91c1c', fontWeight: 700, fontSize: 12, marginTop: 6 }}>
                  {fieldErrors.segmento}
                </p>
              ) : null}
            </div>

            <div>
              <label className="brutalist-label">IMAGEM <span style={{ fontStyle: 'italic' }}>(opcional)</span></label>
              <input type="file" accept="image/*" onChange={handleImagemChange} className="brutalist-input" />
            </div>

            <div className="textarea-wrapper">
              <label className="brutalist-label" htmlFor="campanha-mensagem">MENSAGEM</label>
              <textarea
                id="campanha-mensagem"
                className="brutalist-textarea"
                rows={5}
                placeholder='Ex: "Olá, $cliente! Hoje temos promoções..."'
                aria-describedby={fieldErrors.mensagem ? 'campanha-mensagem-error mensagem-helper' : 'mensagem-helper'}
                aria-invalid={Boolean(fieldErrors.mensagem) || undefined}
                value={mensagem}
                onChange={(e) => {
                  setMensagem(e.target.value);
                  clearFieldError('mensagem');
                }}
                style={fieldErrors.mensagem ? { borderColor: '#b91c1c' } : undefined}
              />
            </div>
            {fieldErrors.mensagem ? (
              <p id="campanha-mensagem-error" role="alert" style={{ color: '#b91c1c', fontWeight: 700, fontSize: 12, marginTop: 6 }}>
                {fieldErrors.mensagem}
              </p>
            ) : null}
            <div id="mensagem-helper" style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
              Dica: inclua <span style={{ fontFamily: 'monospace' }}>$cliente</span> para personalizar o nome.
              Ex: "Olá, <span style={{ fontFamily: 'monospace' }}>$cliente</span>!"
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <div>
                <label className="brutalist-label">RUÍDO</label>
                <input
                  className="brutalist-input narrow"
                  placeholder="Ex: 7"
                  value={ruido}
                  onChange={(e) => setRuido(e.target.value)}
                />
              </div>

              <div>
                <label className="brutalist-label">TENTATIVAS</label>
                <input
                  className="brutalist-input narrow"
                  placeholder="Ex: 3"
                  value={tentativas}
                  onChange={(e) => setTentativas(e.target.value)}
                />
              </div>
            </div>

            <BrutalistButton2 onClick={() => setOpenHorarioModal(true)} className="black">
              DEFINIR HORÁRIOS
            </BrutalistButton2>

            <BrutalistButton2 className="orange" onClick={salvarCampanha}>
              {campanhaEdit?.id ? 'ATUALIZAR CAMPANHA' : 'CRIAR CAMPANHA'}
            </BrutalistButton2>
          </div>
        </div>

        <Modal open={openHorarioModal} onClose={() => setOpenHorarioModal(false)}>
          <Box
            sx={{
              backgroundColor: '#fff',
              padding: '2rem',
              border: '6px solid #000',
              boxShadow: '12px 12px 0 #000',
              width: '100%',
              maxWidth: '700px',
              maxHeight: '90vh',
              overflowY: 'auto',
              margin: '5vh auto',
              fontFamily: 'monospace',
              borderRadius: 0,
            }}
          >
            <h2 style={{
              fontWeight: '900',
              fontSize: '24px',
              marginBottom: '1.5rem',
              textTransform: 'uppercase',
              position: 'relative',
              display: 'inline-block'
            }}>
              Horários da Campanha
              <span style={{
                content: '""',
                display: 'block',
                height: '3px',
                backgroundColor: '#000',
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '100%',
              }}></span>
            </h2>

            {/* Horário global */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
              <div style={{ flex: 1 }}>
                <label className="brutalist-label">INÍCIO</label>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => {
                    setStartTime(e.target.value);
                    clearFieldError('horario');
                  }}
                  className="brutalist-input"
                />
              </div>

              <div style={{ flex: 1 }}>
                <label className="brutalist-label">FIM</label>
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => {
                    setEndTime(e.target.value);
                    clearFieldError('horario');
                  }}
                  className="brutalist-input"
                />
              </div>
            </div>
            {fieldErrors.horario ? (
              <p role="alert" style={{ color: '#b91c1c', fontWeight: 700, fontSize: 12, marginBottom: '1rem' }}>
                {fieldErrors.horario}
              </p>
            ) : null}

            {/* Seleção de dias */}
            <div style={{ marginBottom: '1rem' }}>
              <label className="brutalist-label">DIAS DA SEMANA</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                {diasDaSemana.map((dia) => {
                  const selected = selectedDays.has(dia);
                  return (
                    <button
                      type="button"
                      key={dia}
                      onClick={() => toggleDay(dia)}
                      style={{
                        padding: '0.5rem 0.75rem',
                        border: fieldErrors.dias ? '3px solid #b91c1c' : '3px solid #000',
                        boxShadow: '6px 6px 0 #000',
                        cursor: 'pointer',
                        backgroundColor: selected ? '#000' : '#fff',
                        color: selected ? '#fff' : '#000',
                        fontWeight: 800,
                        textTransform: 'uppercase'
                      }}
                    >
                      {dia}
                    </button>
                  );
                })}
              </div>
              {fieldErrors.dias ? (
                <p role="alert" style={{ color: '#b91c1c', fontWeight: 700, fontSize: 12, marginTop: 6 }}>
                  {fieldErrors.dias}
                </p>
              ) : null}
            </div>

            <BrutalistButton2 onClick={() => setOpenHorarioModal(false)} className="orange">
              SALVAR HORÁRIOS
            </BrutalistButton2>
          </Box>
        </Modal>

        {/* PRÉ-VISUALIZAÇÃO */}
        <div className="preview-container">
          <div className="preview-title">Pré-visualização</div>
          <div className="whatsapp-preview">
            <div className="bubble">
              {preview ? (
                <img src={preview} alt="Preview" className="preview-img" />
              ) : (
                <img src="/350x200.png" alt="Placeholder" className="preview-img" />
              )}

              {mensagem ? (
                mensagem.split('\n').map((line, i) => (
                  <div
                    key={i}
                    dangerouslySetInnerHTML={{
                      __html: formatMarkdownToSafeHtml(line, {
                        style: 'whatsapp',
                        preserveDoubleSpaces: true,
                      }) || '&nbsp;',
                    }}
                  />
                ))
              ) : (
                <div className="whats-msg">
                  Olá! Temos uma super novidade pra você! <br />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </div>
  );
};

export default CriarCampanha;


